import type { OrderFinancialVO } from "../OrderForms/orders";
import { ZUU_FNA_CANONICAL_QUESTION_ORDER, normalizeZuuFnaQuestionList } from "./zuuFnaFieldMap.js";
import { buildZuuQuestionBasedFnaPayload } from "./zuuFnaPayload.js";
import { normalizeOrderFinancialForDisplay } from "./zuuFnaDetailData.js";
import { getLegacyQuestionValueFromList, getQuestionValueFromListByVariant } from "./fnaQuestionAccess.js";

const SHARED_LEGACY_AND_ZUU_QUESTION_KEYS = new Set([
  "insurancePurchaseObjectives",
  "premiumToDisposableIncomeRatio",
  "premiumToLiquidAssetsRatio",
  "willingPremiumPaymentPeriodRange",
  "policyTargetExpectedTime",
]);

const LEGACY_ZUU_FOREIGN_QUESTION_KEYS = new Set([
  ...ZUU_FNA_CANONICAL_QUESTION_ORDER.filter(
    (question) => !SHARED_LEGACY_AND_ZUU_QUESTION_KEYS.has(question),
  ),
  "additionalLifeCoverageAmountOption",
  "additionalLifeCoverageAmountHKD",
  "additionalLifeCoverageAmountUSD",
  "insurancePurchaseMedicalObjectives",
  "additionalCriticalIllnessCoverageAmountOption",
  "additionalCriticalIllnessCoverageAmountHKD",
  "additionalCriticalIllnessCoverageAmountUSD",
  "additionalTargetAmountOption",
  "additionalTargetAmountHKD",
  "additionalTargetAmountUSD",
  "additionalTargetYears",
  "insurancePurchaseOtherObjectiveOptions",
]);

function toQuestionList(list: any[] | undefined) {
  return Array.isArray(list) ? list.filter((question) => question?.question) : [];
}

const removeFromList = (list: any[], keys: Set<string>) =>
  list.filter((question) => !keys.has(question?.question));

function asNumber(value: any): number | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function hasPositiveNumber(value: any) {
  const parsed = asNumber(value);
  return parsed !== undefined && parsed > 0;
}

export function extractFnaDataByVariant(
  financial: Partial<OrderFinancialVO> | null | undefined,
  options: { isZuuMode: boolean },
) {
  if (options.isZuuMode) {
    const normalizedFinancial = normalizeOrderFinancialForDisplay(financial);
    return {
      financialAnalysis: normalizedFinancial.financialAnalysis,
      transferInsuranceDeclaration: normalizedFinancial.transferInsuranceDeclaration,
      suggestionsAndReasons: normalizedFinancial.suggestionsAndReasons,
      chooseProducts: normalizedFinancial.chooseProducts,
      isPremiumFinanced: normalizedFinancial.isPremiumFinanced,
      hasExistingPolicyAsMortgage: normalizedFinancial.hasExistingPolicyAsMortgage,
      premiumFinanceAnalysis: normalizedFinancial.premiumFinanceAnalysis,
      premiumFinancingRepaymentSources:
        normalizedFinancial.premiumFinancingRepaymentSources,
      premiumFinancingRepaymentSourceOther:
        normalizedFinancial.premiumFinancingRepaymentSourceOther,
      outstandingMortgageLoanAmount:
        normalizedFinancial.outstandingMortgageLoanAmount,
      outstandingOtherLoanAmount:
        normalizedFinancial.outstandingOtherLoanAmount,
      premiumFinancingEstimateConsent:
        normalizedFinancial.premiumFinancingEstimateConsent,
      premiumFinancing: normalizedFinancial.premiumFinancing,
      premiumFinancingImportantDisclosureConfirmed:
        normalizedFinancial.premiumFinancingImportantDisclosureConfirmed,
      premiumFinancingRepaymentConfirmed:
        normalizedFinancial.premiumFinancingRepaymentConfirmed,
      onlyProvideOneProduct: normalizedFinancial.onlyProvideOneProduct,
      onlyProvideOneProductReason:
        normalizedFinancial.onlyProvideOneProductReason,
    };
  }

  return {
    financialAnalysis: toQuestionList(financial?.financialAnalysis),
    transferInsuranceDeclaration: toQuestionList(
      financial?.transferInsuranceDeclaration,
    ),
    suggestionsAndReasons: toQuestionList(financial?.suggestionsAndReasons),
    chooseProducts: Array.isArray(financial?.chooseProducts)
      ? financial?.chooseProducts
      : [],
    isPremiumFinanced: financial?.isPremiumFinanced,
    hasExistingPolicyAsMortgage: financial?.hasExistingPolicyAsMortgage,
    premiumFinanceAnalysis: toQuestionList(financial?.premiumFinanceAnalysis as any[]),
    premiumFinancingRepaymentSources: financial?.premiumFinancingRepaymentSources,
    premiumFinancingRepaymentSourceOther:
      financial?.premiumFinancingRepaymentSourceOther,
    outstandingMortgageLoanAmount: financial?.outstandingMortgageLoanAmount,
    outstandingOtherLoanAmount: financial?.outstandingOtherLoanAmount,
    premiumFinancingEstimateConsent: financial?.premiumFinancingEstimateConsent,
    premiumFinancing: financial?.premiumFinancing,
    premiumFinancingImportantDisclosureConfirmed:
      financial?.premiumFinancingImportantDisclosureConfirmed,
    premiumFinancingRepaymentConfirmed:
      financial?.premiumFinancingRepaymentConfirmed,
    onlyProvideOneProduct: financial?.onlyProvideOneProduct,
    onlyProvideOneProductReason: financial?.onlyProvideOneProductReason,
  };
}

export function sanitizeLegacyFnaData(financial: Partial<OrderFinancialVO> | null | undefined) {
  const raw = extractFnaDataByVariant(financial, { isZuuMode: false });
  const financialAnalysis = toQuestionList(raw.financialAnalysis);
  const transferInsuranceDeclaration = toQuestionList(
    raw.transferInsuranceDeclaration,
  );
  const suggestionsAndReasons = toQuestionList(raw.suggestionsAndReasons);

  const removeFinancialAnalysis = new Set<string>(LEGACY_ZUU_FOREIGN_QUESTION_KEYS);
  const objectives = (getLegacyQuestionValueFromList(
    financialAnalysis,
    "insurancePurchaseObjectives",
    "CHECKBOX",
  ) || []) as string[];
  const hasA = objectives.includes("A");
  const hasB = objectives.includes("B");
  const hasC = objectives.includes("C");
  const hasD = objectives.includes("D");
  const hasE = objectives.includes("E");
  const hasF = objectives.includes("F");

  if (!hasA) removeFinancialAnalysis.add("additionalLifeCoverageAmount");
  if (!hasB) {
    removeFinancialAnalysis.add("additionalCriticalIllnessCoverageAmount");
  }
  if (!hasC && !hasD) {
    removeFinancialAnalysis.add("additionalSavingsTargetReturnPeriod");
    removeFinancialAnalysis.add("additionalSavingsTargetReturnAmount");
  }
  if (!hasE) {
    removeFinancialAnalysis.add("additionalInvestmentTargetReturnPeriod");
    removeFinancialAnalysis.add("additionalInvestmentTargetReturnAmount");
    removeFinancialAnalysis.add("investmentOptionManagementPreference");
    removeFinancialAnalysis.add("investmentWithdrawalStartTime");
    removeFinancialAnalysis.add("investmentLongTermGoal");
    removeFinancialAnalysis.add("investmentExpectationIfMarketPoor");
  } else {
    const investmentPreference = getLegacyQuestionValueFromList(
      financialAnalysis,
      "investmentOptionManagementPreference",
      "RADIO",
    );
    if (investmentPreference !== "A") {
      removeFinancialAnalysis.add("investmentWithdrawalStartTime");
      removeFinancialAnalysis.add("investmentLongTermGoal");
      removeFinancialAnalysis.add("investmentExpectationIfMarketPoor");
    }
  }
  if (!hasF) {
    removeFinancialAnalysis.add("insurancePurchaseOtherObjectives");
  }

  const premiumToDisposableIncomeRatio = getLegacyQuestionValueFromList(
    financialAnalysis,
    "premiumToDisposableIncomeRatio",
    "RADIO",
  );
  if (premiumToDisposableIncomeRatio !== "F") {
    removeFinancialAnalysis.add("premiumToDisposableIncomeRatio_F_rate");
  }
  if (premiumToDisposableIncomeRatio !== "G") {
    removeFinancialAnalysis.add("premiumToIncomeRatioNotApplicableReason");
  }

  const premiumToLiquidAssetsRatio = getLegacyQuestionValueFromList(
    financialAnalysis,
    "premiumToLiquidAssetsRatio",
    "RADIO",
  );
  if (premiumToLiquidAssetsRatio !== "F") {
    removeFinancialAnalysis.add("premiumToLiquidAssetsRatio_F_rate");
  }

  const willingPremiumPaymentPeriodRange = getLegacyQuestionValueFromList(
    financialAnalysis,
    "willingPremiumPaymentPeriodRange",
    "RADIO",
  );
  if (willingPremiumPaymentPeriodRange !== "G") {
    removeFinancialAnalysis.add("singlePremiumPaymentLimit");
  }

  const continuedContributionReason = getLegacyQuestionValueFromList(
    financialAnalysis,
    "continuedContributionReason",
    "RADIO",
  );
  if (continuedContributionReason !== "B") {
    removeFinancialAnalysis.add("continuedContributionReasonOther");
  }

  const removeTransfer = new Set<string>();
  const usingExistingPolicyFundsForNewPolicy = getLegacyQuestionValueFromList(
    transferInsuranceDeclaration,
    "usingExistingPolicyFundsForNewPolicy",
    "RADIO",
  );
  if (
    usingExistingPolicyFundsForNewPolicy !== "A" &&
    usingExistingPolicyFundsForNewPolicy !== "B"
  ) {
    removeTransfer.add("intermediaryAssessmentAndComments");
    removeTransfer.add("intermediaryAssessmentAndCommentsOther");
  } else {
    const intermediaryAssessmentAndComments = getLegacyQuestionValueFromList(
      transferInsuranceDeclaration,
      "intermediaryAssessmentAndComments",
      "RADIO",
    );
    if (intermediaryAssessmentAndComments !== "B") {
      removeTransfer.add("intermediaryAssessmentAndCommentsOther");
    }
  }

  const removeSuggestions = new Set<string>();
  const suitabilityDeviationOccurred = getLegacyQuestionValueFromList(
    suggestionsAndReasons,
    "suitabilityDeviationOccurred",
    "RADIO",
  );
  if (suitabilityDeviationOccurred !== "A") {
    removeSuggestions.add("suitabilityDeviationFollowUpYesOptions");
    removeSuggestions.add("reasonForRecommendingDespiteDeviation");
    removeSuggestions.add("otherReasonForRecommendation");
  }
  if (suitabilityDeviationOccurred !== "B") {
    removeSuggestions.add("suitabilityDeviationFollowUpNoOptions");
  }
  if (suitabilityDeviationOccurred === "A") {
    const reasonForRecommendingDespiteDeviation = getLegacyQuestionValueFromList(
      suggestionsAndReasons,
      "reasonForRecommendingDespiteDeviation",
      "RADIO",
    );
    if (reasonForRecommendingDespiteDeviation !== "B") {
      removeSuggestions.add("otherReasonForRecommendation");
    }
  }

  const cleanedFinancialAnalysis = removeFromList(
    financialAnalysis,
    removeFinancialAnalysis,
  );
  const cleanedTransferInsuranceDeclaration = removeFromList(
    transferInsuranceDeclaration,
    removeTransfer,
  );
  const cleanedSuggestionsAndReasons = removeFromList(
    suggestionsAndReasons,
    removeSuggestions,
  );

  const getFinancialQuestion = (
    questionKey: string,
    type: "RADIO" | "CHECKBOX" | "INPUT" = "RADIO",
  ) => getLegacyQuestionValueFromList(cleanedFinancialAnalysis, questionKey, type);

  const insuranceObjectives =
    (getFinancialQuestion("insurancePurchaseObjectives", "CHECKBOX") as string[]) ||
    [];
  const hasObjective = (key: string) => insuranceObjectives.includes(key);
  const medicalObjectives =
    (getFinancialQuestion(
      "insurancePurchaseMedicalObjectives",
      "CHECKBOX",
    ) as string[]) || [];
  const otherObjectiveOptions =
    (getFinancialQuestion(
      "insurancePurchaseOtherObjectiveOptions",
      "CHECKBOX",
    ) as string[]) || [];
  const investmentOptionManagementPreference = getFinancialQuestion(
    "investmentOptionManagementPreference",
    "RADIO",
  );
  const additionalLifeCoverageAmountOption = getFinancialQuestion(
    "additionalLifeCoverageAmountOption",
    "RADIO",
  );
  const additionalCriticalIllnessCoverageAmountOption = getFinancialQuestion(
    "additionalCriticalIllnessCoverageAmountOption",
    "RADIO",
  );
  const additionalTargetAmountOption = getFinancialQuestion(
    "additionalTargetAmountOption",
    "RADIO",
  );
  const premiumToDisposableIncomeRatioMapped = getFinancialQuestion(
    "premiumToDisposableIncomeRatio",
    "RADIO",
  );
  const premiumToLiquidAssetsRatioMapped = getFinancialQuestion(
    "premiumToLiquidAssetsRatio",
    "RADIO",
  );
  const willingPremiumPaymentPeriodRangeMapped = getFinancialQuestion(
    "willingPremiumPaymentPeriodRange",
    "RADIO",
  );
  const policyTargetExpectedTime = getFinancialQuestion(
    "policyTargetExpectedTime",
    "RADIO",
  );
  const selectedProducts = (financial?.chooseProducts || []).filter(
    (product: any) => product?.choose,
  );
  const firstSelectedProduct = selectedProducts[0] || {};
  const additionalLifeCoverageAmountHKD = getFinancialQuestion(
    "additionalLifeCoverageAmountHKD",
    "INPUT",
  );
  const additionalLifeCoverageAmountUSD = getFinancialQuestion(
    "additionalLifeCoverageAmountUSD",
    "INPUT",
  );
  const additionalCriticalIllnessCoverageAmountHKD = getFinancialQuestion(
    "additionalCriticalIllnessCoverageAmountHKD",
    "INPUT",
  );
  const additionalCriticalIllnessCoverageAmountUSD = getFinancialQuestion(
    "additionalCriticalIllnessCoverageAmountUSD",
    "INPUT",
  );
  const additionalTargetAmountHKD = getFinancialQuestion(
    "additionalTargetAmountHKD",
    "INPUT",
  );
  const additionalTargetAmountUSD = getFinancialQuestion(
    "additionalTargetAmountUSD",
    "INPUT",
  );

  const mappedFields = {
    monthlyIncomesTotal:
      asNumber(financial?.monthlyIncomesTotal) ??
      asNumber(financial?.income?.avgMonthlyIncome),
    monthlyExpensesTotal:
      asNumber(financial?.monthlyExpensesTotal) ??
      asNumber(financial?.expense?.avgMonthlyExpense),
    monthlySurplus:
      asNumber(financial?.monthlySurplus) ??
      asNumber(financial?.income?.expectedRetireIncome) ??
      asNumber(
        financial?.zuuStep2Draft?.financialInfo?.availableIncome
          ?.monthlyDisposableIncomeAfterRetirement?.amount,
      ),
    liquidAssetsTotal:
      asNumber(financial?.liquidAssetsTotal) ??
      asNumber(financial?.currentAssets?.currentLiquidAssets),
    additionalLiquidAssetsAtRetirement:
      asNumber(financial?.additionalLiquidAssetsAtRetirement) ??
      asNumber(financial?.currentAssets?.expectedRetireLiquidAssets) ??
      asNumber(
        financial?.zuuStep2Draft?.financialInfo?.liquidAssets
          ?.additionalLiquidAssetsAfterRetirement?.amount,
      ),

    premiumToDisposableIncomeRatio_A: premiumToDisposableIncomeRatioMapped,
    premiumToLiquidAssetsRatio_A: premiumToLiquidAssetsRatioMapped,
    willingPremiumPaymentPeriodRange_A: willingPremiumPaymentPeriodRangeMapped,
    willingPremiumPaymentPeriodRange_G_amount: asNumber(
      getFinancialQuestion("singlePremiumPaymentLimit", "INPUT"),
    ),
    policyTargetExpectedTime,

    insurancePurchaseObjectives_A: hasObjective("A"),
    insurancePurchaseObjectives_B: hasObjective("B"),
    insurancePurchaseObjectives_C: hasObjective("C"),
    insurancePurchaseObjectives_D: hasObjective("D"),
    insurancePurchaseObjectives_E: hasObjective("E"),
    insurancePurchaseObjectives_F: hasObjective("F"),

    insurancePurchaseObjectives_A_ProposedInsured_A:
      additionalLifeCoverageAmountOption === "HKD" ||
      additionalLifeCoverageAmountOption === "A" ||
      (!additionalLifeCoverageAmountOption &&
        hasPositiveNumber(additionalLifeCoverageAmountHKD)),
    insurancePurchaseObjectives_A_ProposedInsured_A_amount: asNumber(
      additionalLifeCoverageAmountHKD,
    ),
    insurancePurchaseObjectives_A_ProposedInsured_B:
      additionalLifeCoverageAmountOption === "USD" ||
      additionalLifeCoverageAmountOption === "B" ||
      (!additionalLifeCoverageAmountOption &&
        hasPositiveNumber(additionalLifeCoverageAmountUSD)),
    insurancePurchaseObjectives_A_ProposedInsured_B_amount: asNumber(
      additionalLifeCoverageAmountUSD,
    ),
    insurancePurchaseObjectives_A_ProposedInsured_C:
      additionalLifeCoverageAmountOption === "N/A" ||
      additionalLifeCoverageAmountOption === "C",

    insurancePurchaseObjectives_B_A: medicalObjectives.includes("A"),
    insurancePurchaseObjectives_B_B: medicalObjectives.includes("B"),
    insurancePurchaseObjectives_B_C: medicalObjectives.includes("C"),
    insurancePurchaseObjectives_B_ProposedInsured_A:
      additionalCriticalIllnessCoverageAmountOption === "HKD" ||
      additionalCriticalIllnessCoverageAmountOption === "A" ||
      (!additionalCriticalIllnessCoverageAmountOption &&
        hasPositiveNumber(additionalCriticalIllnessCoverageAmountHKD)),
    insurancePurchaseObjectives_B_ProposedInsured_A_amount: asNumber(
      additionalCriticalIllnessCoverageAmountHKD,
    ),
    insurancePurchaseObjectives_B_ProposedInsured_B:
      additionalCriticalIllnessCoverageAmountOption === "USD" ||
      additionalCriticalIllnessCoverageAmountOption === "B" ||
      (!additionalCriticalIllnessCoverageAmountOption &&
        hasPositiveNumber(additionalCriticalIllnessCoverageAmountUSD)),
    insurancePurchaseObjectives_B_ProposedInsured_B_amount: asNumber(
      additionalCriticalIllnessCoverageAmountUSD,
    ),

    insurancePurchaseObjectives_E_A:
      investmentOptionManagementPreference === "A",
    insurancePurchaseObjectives_E_B:
      investmentOptionManagementPreference === "B",
    insurancePurchaseObjectives_E_C:
      investmentOptionManagementPreference === "C",

    insurancePurchaseObjectives_CDE_ProposedInsured_A:
      additionalTargetAmountOption === "HKD" ||
      additionalTargetAmountOption === "A" ||
      (!additionalTargetAmountOption && hasPositiveNumber(additionalTargetAmountHKD)),
    insurancePurchaseObjectives_CDE_ProposedInsured_A_amount: asNumber(
      additionalTargetAmountHKD,
    ),
    insurancePurchaseObjectives_CDE_ProposedInsured_B:
      additionalTargetAmountOption === "USD" ||
      additionalTargetAmountOption === "B" ||
      (!additionalTargetAmountOption && hasPositiveNumber(additionalTargetAmountUSD)),
    insurancePurchaseObjectives_CDE_ProposedInsured_B_amount: asNumber(
      additionalTargetAmountUSD,
    ),
    insurancePurchaseObjectives_CDE_year: asNumber(
      getFinancialQuestion("additionalTargetYears", "INPUT"),
    ),

    insurancePurchaseObjectives_F_A: otherObjectiveOptions.includes("A"),
    insurancePurchaseObjectives_F_B: otherObjectiveOptions.includes("B"),
    insurancePurchaseObjectives_F_C: otherObjectiveOptions.includes("C"),
    insurancePurchaseObjectives_F_D: otherObjectiveOptions.includes("D"),
    insurancePurchaseObjectives_F_E: !!getFinancialQuestion(
      "insurancePurchaseOtherObjectives",
      "INPUT",
    ),
    insurancePurchaseObjectives_F_E_remark:
      getFinancialQuestion("insurancePurchaseOtherObjectives", "INPUT") || "",

    insurancePurchaseObjectives_A_1: hasObjective("A"),
    insurancePurchaseObjectives_B_1_1: medicalObjectives.includes("A"),
    insurancePurchaseObjectives_B_2_1: medicalObjectives.includes("B"),
    insurancePurchaseObjectives_B_3_1: medicalObjectives.includes("C"),
    insurancePurchaseObjectives_C_1: hasObjective("C"),
    insurancePurchaseObjectives_D_1: hasObjective("D"),
    insurancePurchaseObjectives_E_1_1:
      investmentOptionManagementPreference === "A",
    insurancePurchaseObjectives_E_2_1:
      investmentOptionManagementPreference === "B",
    insurancePurchaseObjectives_E_3_1:
      investmentOptionManagementPreference === "C",
    insurancePurchaseObjectives_F_1: hasObjective("F"),
    insurancePurchaseObjectives_F_remark_1:
      getFinancialQuestion("insurancePurchaseOtherObjectives", "INPUT") || "",
    policyTargetExpectedTime_A_1: policyTargetExpectedTime === "A",
    policyTargetExpectedTime_B_1: policyTargetExpectedTime === "B",
    policyTargetExpectedTime_C_1: policyTargetExpectedTime === "C",
    policyTargetExpectedTime_D_1: policyTargetExpectedTime === "D",
    policyTargetExpectedTime_E_1: policyTargetExpectedTime === "E",
    policyTargetExpectedTime_F_1: policyTargetExpectedTime === "F",
    policyTargetExpectedTime_G_1: policyTargetExpectedTime === "G",

    chooseProductName_1: firstSelectedProduct?.productName || "",
    chooseProduct_1: firstSelectedProduct?.productCode || "",
  };

  return {
    ...raw,
    ...mappedFields,
    financialAnalysis: cleanedFinancialAnalysis,
    transferInsuranceDeclaration: cleanedTransferInsuranceDeclaration,
    suggestionsAndReasons: cleanedSuggestionsAndReasons,
    premiumFinanceAnalysis: undefined,
    premiumFinancingRepaymentSources: undefined,
    premiumFinancingRepaymentSourceOther: undefined,
    outstandingMortgageLoanAmount: undefined,
    outstandingOtherLoanAmount: undefined,
    premiumFinancingEstimateConsent: undefined,
    premiumFinancing: financial?.hasExistingPolicyAsMortgage
      ? raw.premiumFinancing
      : undefined,
    premiumFinancingImportantDisclosureConfirmed: undefined,
    premiumFinancingRepaymentConfirmed: undefined,
    onlyProvideOneProductReason: financial?.onlyProvideOneProduct
      ? raw.onlyProvideOneProductReason
      : undefined,
  };
}

export function sanitizeZuuFnaData(financial: Partial<OrderFinancialVO> | null | undefined) {
  const raw = extractFnaDataByVariant(financial, { isZuuMode: true });
  const financialAnalysis = normalizeZuuFnaQuestionList(
    toQuestionList(raw.financialAnalysis),
  );
  const suggestionsAndReasons = toQuestionList(raw.suggestionsAndReasons);

  const removeFinancialAnalysis = new Set<string>();
  const objectives = (getQuestionValueFromListByVariant(
    financialAnalysis,
    "insurancePurchaseObjectives",
    "CHECKBOX",
    { isZuuMode: true },
  ) || []) as string[];
  const hasA = objectives.includes("A");
  const hasB = objectives.includes("B");
  const hasC = objectives.includes("C");
  const hasD = objectives.includes("D");
  const hasE = objectives.includes("E");
  const hasF = objectives.includes("F");

  if (!hasA) {
    removeFinancialAnalysis.add("insurancePurchaseObjectives_A_ProposedInsured");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_A_ProposedInsured_A_amount");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_A_ProposedInsured_B_amount");
  }
  if (!hasB) {
    removeFinancialAnalysis.add("insurancePurchaseObjectives_B");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_B_C_ProposedInsured");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_B_C_ProposedInsured_A_amount");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_B_C_ProposedInsured_B_amount");
  }
  if (!hasC && !hasD && !hasE) {
    removeFinancialAnalysis.add("insurancePurchaseObjectives_CDE_ProposedInsured");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_CDE_ProposedInsured_A_amount");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_CDE_ProposedInsured_B_amount");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_CDE_year");
  }
  if (!hasE) {
    removeFinancialAnalysis.add("insurancePurchaseObjectives_E");
  }
  if (!hasF) {
    removeFinancialAnalysis.add("insurancePurchaseObjectives_F");
    removeFinancialAnalysis.add("insurancePurchaseObjectives_F_E_remark");
  } else {
    const otherOption = getQuestionValueFromListByVariant(
      financialAnalysis,
      "insurancePurchaseOtherObjectiveOptions",
      "RADIO",
      { isZuuMode: true },
    );
    if (otherOption !== "E") {
      removeFinancialAnalysis.add("insurancePurchaseObjectives_F_E_remark");
    }
  }

  const willingPremiumPaymentPeriodRange = getQuestionValueFromListByVariant(
    financialAnalysis,
    "willingPremiumPaymentPeriodRange",
    "RADIO",
    { isZuuMode: true },
  );
  if (willingPremiumPaymentPeriodRange !== "G") {
    removeFinancialAnalysis.add("willingPremiumPaymentPeriodRange_G_amount");
  }

  const cleanedFinancialAnalysis = removeFromList(
    financialAnalysis,
    removeFinancialAnalysis,
  );

  const removeSuggestions = new Set<string>();
  const reasonForTheRecommendation = getQuestionValueFromListByVariant(
    suggestionsAndReasons,
    "reasonForTheRecommendation",
    "RADIO",
    { isZuuMode: true },
  );
  if (reasonForTheRecommendation !== "B") {
    removeSuggestions.add("reasonForTheRecommendation_B");
    removeSuggestions.add("reasonForTheRecommendation_B_B1_G_remark");
    removeSuggestions.add("reasonForTheRecommendation_B_B2_E_remark");
  } else {
    const recommendationReasons = (getQuestionValueFromListByVariant(
      suggestionsAndReasons,
      "reasonForTheRecommendation_B",
      "CHECKBOX",
      { isZuuMode: true },
    ) || []) as string[];
    if (!recommendationReasons.includes("B1_G")) {
      removeSuggestions.add("reasonForTheRecommendation_B_B1_G_remark");
    }
    if (!recommendationReasons.includes("B2_E")) {
      removeSuggestions.add("reasonForTheRecommendation_B_B2_E_remark");
    }
  }

  const cleanedSuggestionsAndReasons = removeFromList(
    suggestionsAndReasons,
    removeSuggestions,
  );

  return buildZuuQuestionBasedFnaPayload({
    raw: raw as Partial<OrderFinancialVO>,
    financial: (financial || {}) as Partial<OrderFinancialVO>,
    cleanedFinancialAnalysis,
    cleanedSuggestionsAndReasons,
  });
}
